import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { 
  Sparkles, 
  Download, 
  Trash2, 
  Image, 
  Layers, 
  Eye, 
  RefreshCw, 
  Palette, 
  Maximize2,
  Sliders,
  Check,
  ChevronRight,
  ChevronLeft
} from "lucide-react";
import { BrandsPage } from "@/BrandsPage";
import { ProjectsPage } from "@/ProjectsPage";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const THEMES = {
  editorial_luxury: {
    fontFamilyHeading: "font-playfair",
    fontFamilyBody: "font-manrope",
    bg: "bg-white",
    text: "text-slate-900",
    textMuted: "text-slate-500",
    surface: "bg-slate-50",
    border: "border-slate-200/80",
    accent: "bg-slate-900 text-white hover:bg-slate-800",
    accentSecondary: "bg-white text-slate-900 border border-slate-200 hover:bg-slate-50",
    cardClass: "border border-slate-200/50 rounded-none bg-white",
    containerClass: "max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-12 sm:py-20",
    title: "R&S Luxury Wall Design",
    description: "Transforming raw wall space into architectural poetry. Discover our bespoke vertical lines and custom plaster patterns."
  },
  ultra_minimalist: {
    fontFamilyHeading: "font-space",
    fontFamilyBody: "font-mono",
    bg: "bg-white",
    text: "text-black",
    textMuted: "text-neutral-500",
    surface: "bg-neutral-50",
    border: "border-neutral-300",
    accent: "bg-black text-white hover:bg-neutral-900",
    accentSecondary: "bg-white text-black border border-neutral-300 hover:bg-neutral-100",
    cardClass: "border border-neutral-300 rounded-none bg-white",
    containerClass: "max-w-6xl mx-auto px-8 py-16",
    title: "R&S WALL DESIGN",
    description: "Strict raw grid alignment, architectural negative space, structured patterns."
  },
  warm_earthy: {
    fontFamilyHeading: "font-merriweather",
    fontFamilyBody: "font-work",
    bg: "bg-[#faf9f6]",
    text: "text-[#27272a]",
    textMuted: "text-[#71717a]",
    surface: "bg-[#f3f0e8]",
    border: "border-[#e7e5e4]",
    accent: "bg-[#52525b] text-white hover:bg-[#3f3f46]",
    accentSecondary: "bg-[#f3f0e8] text-[#27272a] border border-[#e7e5e4] hover:bg-[#e7e5e4]/70",
    cardClass: "border border-[#e7e5e4] rounded-md bg-[#faf9f6]",
    containerClass: "max-w-7xl mx-auto px-6 sm:px-10 py-12 sm:py-16",
    title: "R&S Organic Walls",
    description: "Earth-toned clay plasters, natural linen washes, and organic custom textures."
  }
};

const ROOMS = [
  {
    id: "living_room",
    name: "Living Room Space",
    url: "https://images.pexels.com/photos/8490186/pexels-photo-8490186.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    description: "Sofa & potted plant with clean, expansive wall background.",
    wallCoords: "top: 10%, left: 0%, width: 100%, height: 60%"
  },
  {
    id: "bedroom",
    name: "Master Bedroom Suite",
    url: "https://images.pexels.com/photos/8135198/pexels-photo-8135198.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    description: "Minimalist platform bed set against empty wall space.",
    wallCoords: "top: 0%, left: 0%, width: 100%, height: 65%"
  },
  {
    id: "office",
    name: "Creative Office Wall",
    url: "https://images.pexels.com/photos/6373688/pexels-photo-6373688.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    description: "Mockup work table setup with a large gallery backdrop wall.",
    wallCoords: "top: 0%, left: 0%, width: 100%, height: 75%"
  }
];

const PRESETS = [
  "A delicate botanical forest wallpaper in emerald and gold arches",
  "Wabi-sabi concrete plaster textured in washed charcoal gray",
  "Mid-century modern minimalist lines in terracotta and warm sand",
  "Classical botanical illustration with muted sage green flora"
];

function App() {
  const [activeTheme, setActiveTheme] = useState("editorial_luxury");
  const [currentView, setCurrentView] = useState("portfolio");
  const [wallpapers, setWallpapers] = useState([]);
  const [selectedWallpaper, setSelectedWallpaper] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(ROOMS[0]);
  const [blendMode, setBlendMode] = useState("multiply");
  const [opacity, setOpacity] = useState(0.85);
  const [generationPrompt, setGenerationPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [toast, setToast] = useState(null);
  const [lightboxWallpaper, setLightboxWallpaper] = useState(null);

  const themeConfig = THEMES[activeTheme];

  // Fetch wallpapers
  const fetchWallpapers = async (selectFirst = false) => {
    try {
      const response = await axios.get(`${API}/wallpapers`);
      setWallpapers(response.data);
      if (response.data.length > 0 && (selectFirst || !selectedWallpaper)) {
        // Find if we already had a selection, preserve it if possible
        const existing = response.data.find(w => selectedWallpaper && w.id === selectedWallpaper.id);
        setSelectedWallpaper(existing || response.data[0]);
      }
    } catch (error) {
      console.error("Error loading wallpapers:", error);
      showToast("Error loading wallpaper collection. Please try again.", "error");
    }
  };

  useEffect(() => {
    fetchWallpapers(true);
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Handle generation
  const handleGenerate = async (e) => {
    e.preventDefault();
    const prompt = generationPrompt.trim();
    if (!prompt) {
      showToast("Please write a description first.", "error");
      return;
    }

    setGenerating(true);
    showToast("Artisan engine initializing image generation...", "info");

    try {
      const response = await axios.post(`${API}/wallpapers/generate`, { prompt });
      const newWallpaper = response.data;
      
      // Add to wallpapers state immediately
      setWallpapers(prev => [newWallpaper, ...prev]);
      setSelectedWallpaper(newWallpaper);
      setGenerationPrompt("");
      showToast("Artisanal wallpaper generated successfully!", "success");
    } catch (error) {
      console.error("Error generating wallpaper:", error);
      showToast("Failed to generate wallpaper. Elegant fallback styling applied.", "error");
      // Re-fetch in case server saved a fallback
      await fetchWallpapers(false);
    } finally {
      setGenerating(false);
    }
  };

  // Handle deletion
  const handleDelete = async (id, e) => {
    e.stopPropagation(); // Avoid selecting it when deleting
    try {
      await axios.delete(`${API}/wallpapers/${id}`);
      showToast("Wallpaper removed from your custom portfolio.", "success");
      
      // If we deleted the currently selected one, select another
      const remaining = wallpapers.filter(w => w.id !== id);
      setWallpapers(remaining);
      
      if (selectedWallpaper && selectedWallpaper.id === id) {
        setSelectedWallpaper(remaining.length > 0 ? remaining[0] : null);
      }
    } catch (error) {
      console.error("Error deleting wallpaper:", error);
      showToast("Failed to delete wallpaper.", "error");
    }
  };

  const handleDownload = (wp) => {
    if (!wp) return;
    try {
      // For base64 data URIs or standard URLs, we open or trigger download
      const link = document.createElement("a");
      link.href = wp.url;
      link.download = `${wp.name.replace(/\s+/g, "_")}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast(`Initiated download for ${wp.name}`, "success");
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback
      window.open(wp.url, "_blank");
    }
  };

  const filteredWallpapers = activeCategory === "all" 
    ? wallpapers 
    : wallpapers.filter(w => w.category === activeCategory);

  const categories = [
    { id: "all", name: "All Works" },
    { id: "minimalist", name: "Minimalist" },
    { id: "textured", name: "Textured Plasters" },
    { id: "vintage", name: "Vintage & Classic" },
    { id: "modern", name: "Modern Fluid" },
    { id: "generated", name: "Artisan AI Creations" }
  ];

  return (
    <div className={`min-h-screen ${themeConfig.bg} ${themeConfig.text} ${themeConfig.fontFamilyBody} transition-colors duration-500 pb-24`}>
      
      {/* Toast Alert */}
      {toast && (
        <div 
          data-testid="toast-notification"
          className={`fixed top-6 right-6 z-50 p-5 ${themeConfig.cardClass} shadow-xl max-w-sm flex items-start gap-3 animate-fade-in border-l-4 ${
            toast.type === "error" ? "border-l-red-500" : toast.type === "info" ? "border-l-amber-500" : "border-l-green-500"
          }`}
        >
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest font-semibold opacity-60 mb-1">
              System Alert
            </p>
            <p className="text-sm font-medium">{toast.message}</p>
          </div>
        </div>
      )}

      {/* Lightbox / Maximize Modal */}
      {lightboxWallpaper && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col justify-center items-center p-6 animate-fade-in">
          <button 
            data-testid="lightbox-close-btn"
            onClick={() => setLightboxWallpaper(null)}
            className="absolute top-6 right-6 text-white hover:text-neutral-300 font-mono text-xs uppercase tracking-widest"
          >
            [ Close × ]
          </button>
          
          <div className="max-w-4xl max-h-[80vh] w-full flex flex-col items-center">
            <img 
              src={lightboxWallpaper.url} 
              alt={lightboxWallpaper.name}
              className="max-h-[70vh] object-contain border border-neutral-800"
            />
            <div className="mt-6 text-center max-w-xl">
              <h3 className="text-white text-xl font-semibold mb-2">{lightboxWallpaper.name}</h3>
              <p className="text-neutral-400 text-sm mb-4">{lightboxWallpaper.description}</p>
              <button
                onClick={() => handleDownload(lightboxWallpaper)}
                className="px-6 py-2 bg-white text-black hover:bg-neutral-200 text-xs uppercase tracking-widest font-semibold"
              >
                Download Pattern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-black/5 transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 h-20 flex items-center justify-between">
          <a 
            href="https://r&sluxurywalldesign.com" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center gap-3 hover:opacity-85 transition-opacity"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/r09k4vav_B66D2D0A-5855-457C-BDCA-32AB48967377.PNG" 
              alt="R&S LUXURY WALL DESIGN Logo" 
              className="h-12 w-12 object-contain rounded-full border border-black/5 shadow-sm"
            />
            <span className={`text-md sm:text-lg font-bold tracking-tight ${themeConfig.fontFamilyHeading}`}>
              R&S LUXURY WALL DESIGN
            </span>
          </a>

          {/* Theme switcher */}
          <div className="flex items-center gap-4 sm:gap-6">
            {/* Page navigation */}
            <nav className="flex items-center gap-4 sm:gap-5">
              <button
                data-testid="nav-portfolio"
                onClick={() => setCurrentView("portfolio")}
                className={`text-xs uppercase tracking-widest font-semibold transition-all border-b-2 pb-0.5 ${
                  currentView === "portfolio"
                    ? "opacity-100 border-current"
                    : "opacity-50 hover:opacity-100 border-transparent"
                }`}
              >
                Portfolio
              </button>
              <button
                data-testid="nav-work"
                onClick={() => { setCurrentView("work"); window.scrollTo(0, 0); }}
                className={`text-xs uppercase tracking-widest font-semibold transition-all border-b-2 pb-0.5 ${
                  currentView === "work"
                    ? "opacity-100 border-current"
                    : "opacity-50 hover:opacity-100 border-transparent"
                }`}
              >
                Our Work
              </button>
              <button
                data-testid="nav-brands"
                onClick={() => { setCurrentView("brands"); window.scrollTo(0, 0); }}
                className={`text-xs uppercase tracking-widest font-semibold transition-all border-b-2 pb-0.5 ${
                  currentView === "brands"
                    ? "opacity-100 border-current"
                    : "opacity-50 hover:opacity-100 border-transparent"
                }`}
              >
                Our Brands
              </button>
            </nav>

            <div className="flex items-center gap-1 bg-neutral-100/80 p-1 rounded-sm border border-neutral-200/50">
            <button
              data-testid="theme-switcher-luxury"
              onClick={() => setActiveTheme("editorial_luxury")}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition-all duration-300 ${
                activeTheme === "editorial_luxury" 
                  ? "bg-white text-slate-900 shadow-sm" 
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              Luxury
            </button>
            <button
              data-testid="theme-switcher-minimalist"
              onClick={() => setActiveTheme("ultra_minimalist")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider font-semibold transition-all duration-300 ${
                activeTheme === "ultra_minimalist" 
                  ? "bg-black text-white shadow-sm" 
                  : "text-neutral-500 hover:text-black"
              }`}
            >
              Minimal
            </button>
            <button
              data-testid="theme-switcher-earthy"
              onClick={() => setActiveTheme("warm_earthy")}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition-all duration-300 ${
                activeTheme === "warm_earthy" 
                  ? "bg-[#52525b] text-white shadow-sm" 
                  : "text-zinc-500 hover:text-zinc-800"
              }`}
            >
              Earthy
            </button>
          </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={themeConfig.containerClass}>
        {currentView === "brands" ? (
          <BrandsPage themeConfig={themeConfig} />
        ) : currentView === "work" ? (
          <ProjectsPage themeConfig={themeConfig} />
        ) : (
        <>
        {/* Intro */}
        <section className="mb-16 md:mb-24 text-left animate-fade-in">
          <span className="text-xs font-bold uppercase tracking-widest opacity-60 block mb-3">
            ARCHITECTURAL PORTFOLIO
          </span>
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-4 ${themeConfig.fontFamilyHeading}`}>
            {themeConfig.title}
          </h1>
          <p className="text-base sm:text-lg max-w-2xl opacity-75 font-light leading-relaxed">
            {themeConfig.description} Discover our pre-curated collection of plaster textures and classic stripes, or craft a signature wallpaper design dynamically using our integrated AI artisan interface.
          </p>
        </section>

        {/* Visualizer & AI Generator Row */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20">
          
          {/* Room Visualizer (Left Column, 7 Cols) */}
          <div className="lg:col-span-7 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sliders className="w-4 h-4 opacity-60" />
                <h2 className={`text-md uppercase tracking-widest font-semibold ${themeConfig.fontFamilyHeading}`}>
                  1. Room Visualizer
                </h2>
              </div>
              <span className="text-xs opacity-60 font-mono">
                {selectedWallpaper ? `Active: ${selectedWallpaper.name}` : "No Selection"}
              </span>
            </div>

            {/* Visualizer Canvas Card */}
            <div className={`relative aspect-[4/3] w-full overflow-hidden ${themeConfig.cardClass} flex items-center justify-center group`}>
              
              {/* Wallpaper Backdrop Container */}
              {selectedWallpaper ? (
                <div 
                  className="absolute inset-0 z-0 bg-center"
                  style={{
                    backgroundImage: `url(${selectedWallpaper.url})`,
                    backgroundSize: "cover",
                    backgroundRepeat: "repeat",
                  }}
                />
              ) : (
                <div className="absolute inset-0 bg-neutral-100 flex items-center justify-center text-xs tracking-widest opacity-50 font-mono">
                  SELECT WALLPAPER TO PREVIEW
                </div>
              )}

              {/* Room Foreground Overlay */}
              <img 
                src={selectedRoom.url} 
                alt={selectedRoom.name}
                data-testid="wallpaper-visualizer-image"
                className="absolute inset-0 w-full h-full object-cover z-10 select-none pointer-events-none transition-all duration-300"
                style={{
                  mixBlendMode: blendMode,
                  opacity: opacity
                }}
              />

              {/* Custom floating controls in visualizer */}
              <div className="absolute bottom-4 left-4 z-20 bg-black/80 backdrop-blur-md p-3 text-white max-w-xs text-left">
                <p className="text-[10px] uppercase tracking-widest font-semibold text-amber-400 mb-0.5">
                  Artisan View
                </p>
                <p className="text-xs font-bold line-clamp-1">{selectedWallpaper?.name || "No selection"}</p>
                <p className="text-[10px] opacity-60 leading-none">{selectedRoom.name}</p>
              </div>

              {/* Lightbox Trigger */}
              {selectedWallpaper && (
                <button
                  onClick={() => setLightboxWallpaper(selectedWallpaper)}
                  className="absolute top-4 right-4 z-20 p-2 bg-black/70 hover:bg-black/90 text-white rounded-none transition-all"
                  title="View full-screen pattern"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Room Selectors */}
            <div className="grid grid-cols-3 gap-2 mt-4">
              {ROOMS.map((room) => (
                <button
                  key={room.id}
                  data-testid={`room-select-${room.id}`}
                  onClick={() => setSelectedRoom(room)}
                  className={`py-3 px-2 text-center text-xs uppercase tracking-wider font-semibold transition-all ${
                    selectedRoom.id === room.id
                      ? "bg-neutral-900 text-white border-black"
                      : "bg-neutral-100 hover:bg-neutral-200 text-neutral-600 border-transparent"
                  } border`}
                >
                  {room.id.replace("_", " ")}
                </button>
              ))}
            </div>

            {/* Fine Tuning Controls Grid */}
            <div className={`mt-6 p-5 sm:p-6 ${themeConfig.surface} ${themeConfig.border} border`}>
              <h3 className="text-xs uppercase tracking-widest font-bold mb-4 flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 opacity-75" />
                Render Engine Fine-Tuning
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Opacity Control */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs uppercase tracking-wider font-semibold opacity-80">
                      Overlay Intensity
                    </label>
                    <span className="text-xs font-mono font-bold">
                      {Math.round(opacity * 100)}%
                    </span>
                  </div>
                  <input 
                    type="range"
                    min="0.1"
                    max="1.0"
                    step="0.05"
                    value={opacity}
                    data-testid="visualizer-opacity-slider"
                    onChange={(e) => setOpacity(parseFloat(e.target.value))}
                    className="w-full h-1 bg-neutral-200 accent-neutral-800 rounded-lg cursor-pointer"
                  />
                  <p className="text-[10px] opacity-60 mt-1 leading-snug">
                    Calibrates color depth with room natural lighting shadows.
                  </p>
                </div>

                {/* Blend Mode Selector */}
                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold opacity-80 mb-2">
                    Shadow Composition
                  </label>
                  <select
                    value={blendMode}
                    data-testid="visualizer-blend-mode"
                    onChange={(e) => setBlendMode(e.target.value)}
                    className="w-full py-2 px-3 text-xs bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono"
                  >
                    <option value="multiply">Multiply (Shadow Preservation - Best)</option>
                    <option value="overlay">Overlay (High contrast light)</option>
                    <option value="normal">Normal (Matte/Flat cutout)</option>
                  </select>
                  <p className="text-[10px] opacity-60 mt-1 leading-snug">
                    Defines how patterns interact with the sample interior details.
                  </p>
                </div>

              </div>

              {/* Quick actions row */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-black/5">
                <button
                  data-testid="visualizer-download-button"
                  onClick={() => handleDownload(selectedWallpaper)}
                  disabled={!selectedWallpaper}
                  className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5 transition-all ${
                    selectedWallpaper ? themeConfig.accent : "opacity-40 cursor-not-allowed bg-neutral-200 text-neutral-400"
                  }`}
                >
                  <Download className="w-3.5 h-3.5" />
                  Save Texture
                </button>
              </div>
            </div>

          </div>

          {/* AI Wallpaper Generator (Right Column, 5 Cols) */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <h2 className={`text-md uppercase tracking-widest font-semibold ${themeConfig.fontFamilyHeading}`}>
                2. AI Wallpaper Artisan
              </h2>
            </div>

            <div className={`p-6 md:p-8 ${themeConfig.surface} ${themeConfig.border} border flex-1 flex flex-col justify-between`}>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold opacity-60 mb-2">
                  CUSTOM WALLPAPER DESIGNER
                </p>
                <p className="text-sm opacity-75 leading-relaxed mb-6">
                  Compose a personalized wallpaper pattern. Our integrated artisan model generates tileable structural wallpaper images directly inside your session database.
                </p>

                {/* Generator Form */}
                <form onSubmit={handleGenerate} className="space-y-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider font-semibold mb-2 opacity-80">
                      Design Prompt
                    </label>
                    <textarea
                      value={generationPrompt}
                      data-testid="wallpaper-prompt-input"
                      onChange={(e) => setGenerationPrompt(e.target.value)}
                      placeholder="Describe your desired style... e.g. Vintage art deco flowers in soft terracotta and olive green..."
                      disabled={generating}
                      rows={4}
                      className="w-full p-4 text-sm bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900 resize-none font-light placeholder:opacity-50"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="wallpaper-generate-button"
                    disabled={generating}
                    className={`w-full py-4 text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 transition-all ${
                      generating ? "bg-neutral-300 text-neutral-500 cursor-wait" : themeConfig.accent
                    }`}
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Generating Artistry...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate Custom Pattern
                      </>
                    )}
                  </button>
                </form>

                {/* Loading state bar decoration */}
                {generating && (
                  <div className="mt-4 space-y-1 animate-pulse">
                    <div className="w-full bg-neutral-200 h-1">
                      <div className="bg-amber-500 h-1 animate-[shimmer_2s_infinite]" style={{ width: "60%" }} />
                    </div>
                    <span className="text-[10px] font-mono opacity-50 block text-right">
                      Processing high-quality render...
                    </span>
                  </div>
                )}
              </div>

              {/* Inspiration Presets */}
              <div className="mt-8 pt-6 border-t border-black/5">
                <h4 className="text-[11px] uppercase tracking-widest font-bold opacity-60 mb-3">
                  Design Inspiration Prompts
                </h4>
                <div className="space-y-2">
                  {PRESETS.map((preset, idx) => (
                    <button
                      key={idx}
                      onClick={() => setGenerationPrompt(preset)}
                      disabled={generating}
                      className="w-full text-left p-3 text-xs bg-white hover:bg-neutral-50 border border-neutral-200/60 flex items-center justify-between group transition-all"
                    >
                      <span className="line-clamp-1 opacity-70 group-hover:opacity-100 font-light">
                        {preset}
                      </span>
                      <ChevronRight className="w-3.5 h-3.5 opacity-40 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

          </div>

        </section>

        {/* Wallpaper Portfolio Gallery Section */}
        <section className="mt-20 border-t border-black/5 pt-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
            <div className="text-left">
              <span className="text-xs font-bold uppercase tracking-widest opacity-60 block mb-2">
                COLLECTION DIRECTORY
              </span>
              <h2 className={`text-2xl sm:text-3xl font-light tracking-tight ${themeConfig.fontFamilyHeading}`}>
                Editorial Design Wallpaper Gallery
              </h2>
            </div>

            {/* Category Filter Controls */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  data-testid={`filter-category-${cat.id}`}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-all ${
                    activeCategory === cat.id
                      ? "border-b-2 border-neutral-900 opacity-100"
                      : "opacity-55 hover:opacity-100 border-b-2 border-transparent"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Bento / Asymmetric Grid Portfolio */}
          {filteredWallpapers.length === 0 ? (
            <div className={`p-16 text-center ${themeConfig.cardClass} opacity-60`}>
              <Image className="w-8 h-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-mono tracking-widest uppercase">No wallpapers in this category yet.</p>
              <p className="text-xs font-light mt-1">Generate some dynamic models above to see them in this space.</p>
            </div>
          ) : (
            <div 
              data-testid="wallpaper-gallery"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 animate-fade-in"
            >
              {filteredWallpapers.map((wp, index) => {
                const isSelected = selectedWallpaper && selectedWallpaper.id === wp.id;
                const isGenerated = wp.category === "generated";

                return (
                  <div
                    key={wp.id || index}
                    data-testid={`wallpaper-card-${wp.id || index}`}
                    onClick={() => setSelectedWallpaper(wp)}
                    className={`group cursor-pointer flex flex-col transition-all duration-300 ${
                      isSelected ? "ring-2 ring-neutral-800 ring-offset-4" : ""
                    } ${themeConfig.cardClass} relative overflow-hidden`}
                  >
                    {/* Visual Card Image */}
                    <div className="relative aspect-[3/4] w-full overflow-hidden bg-neutral-100">
                      <img 
                        src={wp.url} 
                        alt={wp.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      
                      {/* Hover Overlay Button Panels */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLightboxWallpaper(wp);
                            }}
                            className="p-2 bg-white/95 text-neutral-800 hover:bg-white rounded-none transition-all"
                            title="Expand view"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(wp);
                            }}
                            className="p-2 bg-white/95 text-neutral-800 hover:bg-white rounded-none transition-all"
                            title="Download pattern file"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {isGenerated && (
                            <button
                              data-testid={`wallpaper-delete-btn-${wp.id || index}`}
                              onClick={(e) => handleDelete(wp.id, e)}
                              className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-none transition-all"
                              title="Delete from collection"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* Apply selection feedback */}
                        <div className="w-full">
                          <button
                            data-testid={`wallpaper-preview-btn-${wp.id || index}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedWallpaper(wp);
                              showToast(`Applied ${wp.name} to rendering visualizer.`, "success");
                            }}
                            className="w-full py-2.5 bg-white text-black font-semibold text-xs uppercase tracking-widest hover:bg-neutral-100 transition-colors"
                          >
                            Preview on Wall
                          </button>
                        </div>
                      </div>

                      {/* Tag badges */}
                      <div className="absolute top-3 left-3 flex flex-col gap-1.5 pointer-events-none">
                        <span className="bg-black/75 backdrop-blur-sm text-[10px] text-white font-mono px-2 py-1 uppercase tracking-widest font-semibold">
                          {wp.category}
                        </span>
                        {isSelected && (
                          <span className="bg-emerald-600 text-[10px] text-white font-mono px-2 py-1 uppercase tracking-widest font-semibold flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Active
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Meta info text */}
                    <div className="p-6 text-left flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className={`text-lg font-light leading-tight mb-2 group-hover:text-neutral-500 transition-colors ${themeConfig.fontFamilyHeading}`}>
                          {wp.name}
                        </h3>
                        <p className="text-xs opacity-70 leading-relaxed font-light mb-4">
                          {wp.description}
                        </p>
                      </div>

                      {/* Details specs */}
                      <div className="flex items-center justify-between pt-3 border-t border-black/5 mt-auto">
                        <span className="text-[10px] opacity-50 font-mono">
                          ID: {wp.id ? wp.id.slice(-6).toUpperCase() : `GEN-${index}`}
                        </span>
                        {wp.prompt && (
                          <span 
                            className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-sm line-clamp-1 max-w-[150px] font-mono cursor-help"
                            title={`Prompt: ${wp.prompt}`}
                          >
                            &ldquo;{wp.prompt}&rdquo;
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        </>
        )}
      </main>

      {/* Editorial Footer */}
      <footer className="mt-32 pt-16 pb-12 border-t border-black/5 text-center">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <p className={`text-md tracking-wider font-semibold mb-2 ${themeConfig.fontFamilyHeading}`}>
            R&S LUXURY WALL DESIGN
          </p>
          <p className="text-xs opacity-55 max-w-md mx-auto leading-relaxed mb-6 font-light">
            Crafting raw spaces into sublime editorial designs through minimalist layout theory and advanced generative artisan technology.
          </p>
          <div className="flex justify-center gap-4 text-xs opacity-40 font-mono mb-8">
            <span className="hover:opacity-100 transition-opacity">EST. 2026</span>
            <span>•</span>
            <span className="hover:opacity-100 transition-opacity">SHARP MINIMALISM</span>
            <span>•</span>
            <span className="hover:opacity-100 transition-opacity">ARTISAN CODES</span>
          </div>
          <p className="text-[10px] opacity-40 font-mono">
            © 2026 R&S Luxury Wall Design. All Rights Reserved. Powered by OpenAI Image Generation.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
